import useGAnalytics, {
  EventAction,
  EventCategory,
} from "@/libs/hooks/tracking/useGAnalytics";
import useVisibilityTracker from "@/libs/hooks/tracking/useVisibilityTracking";
import { useCopies } from "@/libs/hooks/useCopy";
import { useMainDict } from "@/libs/hooks/useTranslation";
import { IconMapPin } from "@tabler/icons-react";

export const FindUsAndMapSection = () => {
  const { t } = useMainDict();

  const { trackEvent } = useGAnalytics();

  const { copy } = useCopies();

  useVisibilityTracker("map-section", () => {
    trackEvent(
      "map-section-visible",
      EventCategory.USER_INTERACTION,
      EventAction.BECOMES_VISIBLE
    );
  });

  const handleMapClick = () => {
    trackEvent(
      "map-clicked",
      EventCategory.USER_INTERACTION,
      EventAction.CLICK,
      "Map"
    );
  };

  return (
    <>
      {/* Find Us Banner */}
      <section
        id="map-section"
        className="relative w-full overflow-hidden"
        aria-labelledby="find-us-heading"
      >
        {/* Vintage terracotta background */}
        <div className="absolute inset-0 bg-gradient-to-br from-vintage-terracotta via-[#B85539] to-[#A34B32]">
          {/* Pattern overlay */}
          <div
            className="absolute inset-0 opacity-[0.05]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='52' height='26' viewBox='0 0 52 26' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M10 10c0-2.21-1.79-4-4-4-3.314 0-6-2.686-6-6h2c0 2.21 1.79 4 4 4 3.314 0 6 2.686 6 6 0 2.21 1.79 4 4 4 3.314 0 6 2.686 6 6 0 2.21 1.79 4 4 4v2c-3.314 0-6-2.686-6-6 0-2.21-1.79-4-4-4-3.314 0-6-2.686-6-6zm25.464-1.95l8.486 8.486-1.414 1.414-8.486-8.486 1.414-1.414z' /%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
          {/* Vignette */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_40%,rgba(0,0,0,0.25)_100%)]" />
        </div>

        {/* Decorative top border */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-vintage-cream/40 to-transparent" />

        <div className="relative z-10 py-20 md:py-28 px-4">
          <div className="max-w-4xl mx-auto text-center">
            {/* Decorative element */}
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="w-16 h-px bg-vintage-cream/40" />
              <IconMapPin size={32} className="text-vintage-cream" />
              <div className="w-16 h-px bg-vintage-cream/40" />
            </div>

            <h2
              id="find-us-heading"
              className="font-script text-4xl md:text-6xl lg:text-7xl text-vintage-cream transform -rotate-1"
              style={{ textShadow: "2px 2px 4px rgba(0,0,0,0.3)" }}
            >
              {copy?.wypadnijNaPlacekCopy}
            </h2>

            {/* Address info */}
            <div className="mt-8 flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8">
              <div className="flex items-center gap-2 text-vintage-cream/90 font-serif">
                <span className="text-lg">Kalwaryjska 3, Kraków</span>
              </div>
            </div>

            {/* Decorative element */}
            <div className="flex items-center justify-center gap-4 mt-8">
              <div className="w-12 h-px bg-vintage-cream/40" />
              <span className="text-vintage-cream/60 text-lg">◆</span>
              <div className="w-12 h-px bg-vintage-cream/40" />
            </div>
          </div>
        </div>

        {/* Decorative bottom border */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-vintage-cream/40 to-transparent" />
      </section>

      {/* Map Section */}
      <section aria-labelledby="map-heading" className="relative">
        <h2 id="map-heading" className="sr-only">
          {t("mapSectionTitle")}
        </h2>

        {/* Vintage frame around map */}
        <div className="relative">
          {/* Top decorative border */}
          <div className="absolute top-0 left-0 right-0 h-3 bg-vintage-espresso z-10 flex items-center justify-center">
            <div className="flex items-center gap-4">
              <div className="w-8 h-px bg-vintage-ochre/50" />
              <div className="w-2 h-2 bg-vintage-ochre/50 rotate-45" />
              <div className="w-8 h-px bg-vintage-ochre/50" />
            </div>
          </div>

          {/* Bottom decorative border */}
          <div className="absolute bottom-0 left-0 right-0 h-3 bg-vintage-espresso z-10 flex items-center justify-center">
            <div className="flex items-center gap-4">
              <div className="w-8 h-px bg-vintage-ochre/50" />
              <div className="w-2 h-2 bg-vintage-ochre/50 rotate-45" />
              <div className="w-8 h-px bg-vintage-ochre/50" />
            </div>
          </div>

          <iframe
            className="w-full md:h-[500px] h-[400px]"
            id="gmap_canvas"
            title={t("mapTitle")}
            frameBorder="0"
            scrolling="no"
            marginHeight={0}
            marginWidth={0}
            onClick={handleMapClick}
            src="https://maps.google.com/maps?width=100%25&amp;height=600&amp;hl=en&amp;q=Pizzicletta,%20Krakow+(Pizzicletta)&amp;t=&amp;z=15&amp;ie=UTF8&amp;iwloc=B&amp;output=embed"
            style={{ filter: "sepia(15%) saturate(90%))" }}
          />
        </div>
      </section>
    </>
  );
};
